import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Bell } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { toast } from 'sonner';

export default function NotificationBell({
  notificationsUnread,
  badgeCounts,
  notificationItems,
  navigate,
  refreshNotifications,
}) {
  const [markingAll, setMarkingAll] = useState(false);

  const handleMarkAllRead = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (notificationsUnread <= 0) return;
    setMarkingAll(true);
    try {
      await api.post('/notifications/read-all');
      await refreshNotifications?.();
    } catch {
      toast.error('Could not update notifications');
    } finally {
      setMarkingAll(false);
    }
  };

  const handleNotificationClick = async (n, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (n?.id) {
        await api.post(`/notifications/${n.id}/read`);
      }
    } catch {
      /* still navigate */
    }
    await refreshNotifications?.();
    if (n.meta?.path) {
      navigate(n.meta.path);
      return;
    }
    if (n.contact_id) {
      navigate(`/inbox?contact=${n.contact_id}`);
    }
  };

  const hasDbNotifications = notificationItems.length > 0;
  const showMarkAll = notificationsUnread > 0;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) refreshNotifications?.();
      }}
    >
      <DropdownMenuTrigger asChild>
        <button type="button" className="relative p-2 rounded-md hover:bg-muted" data-testid="notifications-btn">
          <Bell size={20} />
          {notificationsUnread > 0 && (
            <span className="absolute top-1 right-1 min-w-[1rem] h-4 px-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {notificationsUnread > 9 ? '9+' : notificationsUnread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="px-3 py-2 border-b border-border flex items-center justify-between gap-2">
          <h4 className="font-semibold text-sm">Notifications</h4>
          {showMarkAll ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs shrink-0"
              disabled={markingAll}
              onClick={handleMarkAllRead}
              data-testid="notifications-mark-all-read"
            >
              {markingAll ? '…' : 'Mark all read'}
            </Button>
          ) : null}
        </div>
        <div className="max-h-80 overflow-auto">
          {badgeCounts.call_requests > 0 && (
            <div
              className="px-3 py-2.5 bg-red-500/5 border-b border-border cursor-pointer hover:bg-red-500/10 transition-colors"
              onClick={() => navigate('/dashboard')}
              role="button"
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <p className="text-sm font-medium text-red-600">
                  {badgeCounts.call_requests} call request{badgeCounts.call_requests > 1 ? 's' : ''} pending
                </p>
              </div>
            </div>
          )}
          {badgeCounts.unread_inbox > 0 && (
            <div
              className="px-3 py-2.5 bg-blue-500/5 border-b border-border cursor-pointer hover:bg-blue-500/10 transition-colors"
              onClick={() => navigate('/inbox')}
              role="button"
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full" />
                <p className="text-sm">
                  {badgeCounts.unread_inbox} unread message{badgeCounts.unread_inbox > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          )}
          {badgeCounts.pending_followups > 0 && (
            <div
              className="px-3 py-2.5 border-b border-border cursor-pointer hover:bg-muted transition-colors"
              onClick={() => navigate('/followups')}
              role="button"
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-amber-500 rounded-full" />
                <p className="text-sm">
                  {badgeCounts.pending_followups} follow-up{badgeCounts.pending_followups > 1 ? 's' : ''} pending
                </p>
              </div>
            </div>
          )}
          {notificationItems.slice(0, 8).map((n) => (
            <div
              key={n.id}
              className={cn(
                'px-3 py-2.5 border-b border-border text-left hover:bg-muted/80 transition-colors cursor-pointer',
                !n.read && 'bg-primary/5',
              )}
              onClick={(e) => handleNotificationClick(n, e)}
              role="button"
            >
              <p className="text-xs font-medium line-clamp-1">{n.title}</p>
              <p className="text-[10px] text-muted-foreground line-clamp-2">{n.message}</p>
            </div>
          ))}
          {(badgeCounts.call_requests || 0) === 0 &&
            (badgeCounts.unread_inbox || 0) === 0 &&
            (badgeCounts.pending_followups || 0) === 0 &&
            !hasDbNotifications && (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">All caught up</div>
            )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
